import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ProjectForm } from './ProjectForm';
import { useClients } from '../../hooks/useClients';
import type {
  ProjectWithRelations,
  CreateProjectRequest,
  UpdateProjectRequest,
} from '../../types/project';

interface ProjectDetailModalProps {
  open: boolean;
  onClose: () => void;
  project?: ProjectWithRelations | null;
  onSave: (data: CreateProjectRequest | UpdateProjectRequest) => Promise<void>;
  isLoading?: boolean;
}

export const ProjectDetailModal: React.FC<ProjectDetailModalProps> = ({
  open,
  onClose,
  project,
  onSave,
  isLoading = false,
}) => {
  const isEditing = !!project;
  const [formData, setFormData] = useState<CreateProjectRequest | UpdateProjectRequest>({});

  const { data: clientsData } = useClients();
  const clients = clientsData?.clients || [];

  const handleFormChange = (data: CreateProjectRequest | UpdateProjectRequest) => {
    setFormData(data);
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) return;

    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving project:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isEditing ? 'Edit Project' : 'New Project'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <ProjectForm
          project={project}
          onChange={handleFormChange}
          clients={clients}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!formData.name?.trim() || isLoading}
        >
          {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectDetailModal;
